import AsyncStorage from "@react-native-async-storage/async-storage";
import { Platform } from 'react-native';
import { generateObjectId } from "./objectId";
import { getAccessToken } from "./storage";

const API_URL = 'https://api.happygastro.hu/api';

/**
 * Error reporting service to log errors to the server
 */
class ErrorReportingService {
  /**
   * Report an error to the server
   * @param {Error|string} error - The error object or error message
   * @param {Object} additionalInfo - Additional information about the error context
   * @returns {Promise<void>}
   */
  static async reportError(error, additionalInfo = {}) {
    try {
      const errorId = generateObjectId();
      const restaurantId = await this.getRestaurantId();

      // Extract error message
      const errorMessage = error instanceof Error ? error.message : String(error);

      // Extract stack trace if available
      const stackTrace = error instanceof Error ? error.stack : '';

      // Try to parse the error as JSON if it's a string
      let fullJSON = null;
      try {
        if (typeof error === 'string') {
          fullJSON = JSON.parse(error);
        } else if (error instanceof Response) {
          fullJSON = await error.clone().json();
        } else if (error instanceof Error && error.response) {
          fullJSON = error.response;
        }
      } catch (e) {
        // If parsing fails, just use the error as is
        fullJSON = error instanceof Error ? { message: error.message } : error;
      }

      // Extract error code if available
      const errorCode = error.code || error.status || error.statusCode ||
          (error.response && error.response.status) ||
          (fullJSON && fullJSON.code) || 'unknown';

      // Prepare the error report payload
      const errorReport = {
        restaurantId: restaurantId || 'unknown',
        error: errorMessage,
        errorId: errorId,
        errorInfo: {
          errorCode: errorCode,
          errorString: errorMessage,
          stacktrace: stackTrace,
          fullJSON: fullJSON,
          message: errorMessage,
          ...additionalInfo
        },
        extraInfo: {
          orderCode: additionalInfo.orderCode || additionalInfo.order?.orderCode,
          generatedQRCode: additionalInfo.generatedQRCode,
          order: additionalInfo.order,
          stackTrace,
          timestamp: new Date().toISOString(),
          platform: Platform.OS,
          ...additionalInfo
        }
      };

      // Send the error report to the server
      await this.sendErrorReport(errorReport);

      console.log(`Error reported with ID: ${errorId}`);
      return errorId;
    } catch (reportingError) {
      // Don't throw from the error reporter to avoid infinite loops
      console.error('Failed to report error:', reportingError);
    }
  }

  /**
   * Get the restaurant ID from AsyncStorage
   * @returns {Promise<string|null>}
   */
  static async getRestaurantId() {
    try {
      const restaurantData = await AsyncStorage.getItem('restaurant');
      if (restaurantData) {
        const restaurant = JSON.parse(restaurantData);
        return restaurant._id;
      }
      return null;
    } catch (error) {
      console.error('Error fetching restaurant ID:', error);
      return null;
    }
  }

  /**
   * Send the error report to the server
   * @param {Object} errorReport - The error report payload
   * @returns {Promise<void>}
   */
  static async sendErrorReport(errorReport) {
    try {
      const accessToken = await getAccessToken();

      const response = await fetch(`${API_URL}/error/log`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': accessToken ? `Bearer ${accessToken}` : '',
        },
        body: JSON.stringify(errorReport),
      });

      if (!response.ok) {
        throw new Error(`Error reporting failed with status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error sending error report:', error);
      throw error;
    }
  }

  /**
   * Log information to console and prepare for future API storage
   * @param {string} level - Log level (info, warn, error)
   * @param {string} message - Log message
   * @param {Object} data - Additional data to log
   * @param {string} source - Source of the log (component, function, etc.)
   * @returns {void}
   */
  static log(level = 'info', message, data = {}, source = '') {
    // Format the log message
    const timestamp = new Date().toISOString();
    const formattedMessage = `[${timestamp}] [${level.toUpperCase()}] ${source ? `[${source}]` : ''} ${message}`;

    // Log to console based on level
    switch (level.toLowerCase()) {
      case 'error':
        console.error(formattedMessage, data);
        break;
      case 'warn':
        console.warn(formattedMessage, data);
        break;
      case 'info':
      default:
        console.log(formattedMessage, data);
        break;
    }

    // TODO: In the future, this will also send logs to the API
    // For now, we're just logging to the console
  }

  /**
   * Convenience method for info logs
   * @param {string} message - Log message
   * @param {Object} data - Additional data to log
   * @param {string} source - Source of the log
   */
  static info(message, data = {}, source = '') {
    this.log('info', message, data, source);
  }

  /**
   * Convenience method for warning logs
   * @param {string} message - Log message
   * @param {Object} data - Additional data to log
   * @param {string} source - Source of the log
   */
  static warn(message, data = {}, source = '') {
    this.log('warn', message, data, source);
  }

  /**
   * Convenience method for error logs
   * @param {string} message - Log message
   * @param {Object} data - Additional data to log
   * @param {string} source - Source of the log
   */
  static error(message, data = {}, source = '') {
    this.log('error', message, data, source);
  }
}

export default ErrorReportingService;
